import React, {useEffect, useMemo, useState} from "react";
import {SelectChangeEvent} from "@mui/material";
import ConsentController from "@source/api/controllers/consentController";
import {
  ConsentCategory,
  ConsentDocument,
  ConsentDocumentQuery,
  ConsentDocumentStatus,
} from "@source/api/models/consentModels";
import {
  consentDocumentDetailPath,
  consentDocumentEditPath,
  ConsentHistoryFilters,
  ConsentHistoryStatusFilter,
  createConsentDocumentHistoryQuery,
} from "@source/api/models/consentHelpers";
import DateRangePicker from "@source/components/UI/inputs/dateRangePicker/dateRangePicker";
import SearchInput from "@source/components/UI/inputs/searchInput/searchInput";
import SelectInput, {ISelectItem} from "@source/components/UI/inputs/selectInput/selectInput";
import {IFillVariant} from "@source/utils/enums";
import useIncrementalConsentList from "@source/hooks/useIncrementalConsentList";
import {ConsentDocumentCard} from "./consentDocumentCard";
import {ConsentAutoLoad} from "./consentAutoLoad";
import {ConsentFilterBar} from "./consentFilterBar";
import {ConsentRequestState} from "./consentRequestState";
import styles from "./consentDocumentHistory.module.scss";

const statusOptions: ISelectItem[] = [
  {id: "all", value: "all", title: "All statuses"},
  {id: "draft", value: "lifecycle:draft", title: "Draft"},
  {id: "awaiting-signatures", value: "lifecycle:awaiting_signatures", title: "Awaiting Signatures"},
  {id: "awaiting-employee-signatures", value: "stage:awaiting_employee_signatures", title: "Awaiting Employee Signatures"},
  {id: "awaiting-family-signatures", value: "stage:awaiting_family_consent", title: "Awaiting Family Signatures"},
  {id: "ready-for-resident-presentation", value: "stage:ready_for_resident_presentation", title: "Ready for Resident Presentation"},
  {id: "awaiting-clinical-signature", value: "stage:awaiting_clinical_signature", title: "Awaiting GP / Clinician Signature"},
  {id: "completed", value: "lifecycle:completed", title: "Completed"},
  {id: "declined", value: "lifecycle:declined", title: "Declined"},
  {id: "withdrawal-requested", value: "withdrawal:requested", title: "Withdrawal requested"},
  {id: "withdrawn", value: "lifecycle:withdrawn", title: "Withdrawn"},
];

const asOption = (id: string, title: string): ISelectItem => ({id, value: id, title});

export const ConsentDocumentHistory = ({
  nursingHomeId,
  organizationId,
  residentId,
  canManage,
  showFilters = true,
}: {
  nursingHomeId: string;
  organizationId?: string;
  residentId?: string;
  canManage: boolean;
  showFilters?: boolean;
}) => {
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Omit<ConsentHistoryFilters, "search" | "sort">>({statusFilter: "all"});
  const [categories, setCategories] = useState<ConsentCategory[]>([]);
  const [categoryError, setCategoryError] = useState("");
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchText), 500);
    return () => window.clearTimeout(timeout);
  }, [searchText]);

  useEffect(() => {
    if (!showFilters) return;

    let active = true;
    const loadOptions = async () => {
      setOptionsLoading(true);
      setCategoryError("");
      const requests: Promise<void>[] = [];
      if (organizationId) {
        requests.push(ConsentController.listCategories(organizationId, {page: 0, limit: 100, sort: "desc"})
          .then((items) => { if (active) setCategories(items); })
          .catch((error: unknown) => { if (active) setCategoryError(error instanceof Error ? error.message : "Unable to load Consent Categories."); }));
      }
      await Promise.all(requests);
      if (active) setOptionsLoading(false);
    };
    loadOptions();
    return () => { active = false; };
  }, [organizationId, showFilters]);

  const query = useMemo(
    () => createConsentDocumentHistoryQuery(
      showFilters ? {...filters, search, residentId, sort: "desc"} : {residentId, sort: "desc"},
    ),
    [filters, residentId, search, showFilters],
  );
  const request = useMemo(
    () => (pageQuery: ConsentDocumentQuery & {page: number; limit: number}) =>
      ConsentController.listDocuments(nursingHomeId, pageQuery),
    [nursingHomeId],
  );
  const list = useIncrementalConsentList<ConsentDocument, ConsentDocumentQuery>(request, query);
  const categoryOptions = useMemo(
    () => [asOption("", "All Consent Categories"), ...categories.map((category) => asOption(category._id, category.name))],
    [categories],
  );
  const hasFilters = showFilters && Boolean(
    searchText
    || (filters.statusFilter && filters.statusFilter !== "all")
    || filters.categoryId
    || filters.dateRange,
  );
  const clearFilters = () => {
    setSearchText("");
    setSearch("");
    setFilters({statusFilter: "all"});
  };
  const updateFilter = (key: "statusFilter" | "categoryId", event: SelectChangeEvent<string>) =>
    setFilters((current) => ({
      ...current,
      [key]: key === "statusFilter"
        ? event.target.value as ConsentHistoryStatusFilter
        : event.target.value || undefined,
    } as Omit<ConsentHistoryFilters, "search" | "sort">));

  return <div className={styles.history}>
    {showFilters && <>
      <ConsentFilterBar onClear={hasFilters ? clearFilters : undefined}>
        <SearchInput id="consent-search" className={styles.search} value={searchText} onChange={(event) => setSearchText(event.target.value)} placeHolder="Search" fill={IFillVariant.Fill}/>
        <SelectInput id="consent-status" value={filters.statusFilter || "all"} handleChange={(event) => updateFilter("statusFilter", event)} items={statusOptions} placeholder="Status"/>
        <SelectInput id="consent-category" value={filters.categoryId || ""} handleChange={(event) => updateFilter("categoryId", event)} items={categoryOptions} placeholder={categoryError ? "Categories unavailable" : "Consent Category"} disabled={Boolean(categoryError)}/>
        <DateRangePicker className={styles.dateRange} id="consent-date-range" value={filters.dateRange || null} onChange={(dateRange) => setFilters((current) => ({...current, dateRange}))} placeholder="Date range" maxDate={new Date()} fill={IFillVariant.Fill}/>
      </ConsentFilterBar>
      {(optionsLoading || categoryError) && <div className={styles.optionState}>
        {optionsLoading ? "Loading filter options…" : null}
        {categoryError ? `Consent Categories unavailable: ${categoryError}` : null}
      </div>}
    </>}
    {list.isLoading && !list.items.length ? <ConsentRequestState variant="loading" title="Loading consent documents"/> : null}
    {list.error && !list.items.length ? <ConsentRequestState variant="error" title="Unable to load consent documents" description={list.error} onRetry={list.retry}/> : null}
    {!list.isLoading && !list.error && !list.items.length ? <ConsentRequestState variant="empty" title={hasFilters ? "No matching consent documents" : residentId ? "No consent documents for this resident" : "No consent documents yet"} description={hasFilters ? "Try changing or clearing your filters." : "Consent documents will appear here when they are created."}/> : null}
    {list.items.length ? <div className={styles.list}>{list.items.map((document) => (
      <ConsentDocumentCard
        key={document._id}
        document={document}
        variant={document.status === ConsentDocumentStatus.DRAFT && canManage ? "editableDraft" : "default"}
        link={document.status === ConsentDocumentStatus.DRAFT && canManage
          ? consentDocumentEditPath(document.resident._id, document._id)
          : consentDocumentDetailPath(document._id)}
      />
    ))}</div> : null}
    {list.error && list.items.length ? <ConsentRequestState variant="error" title="Unable to load more consent documents" description={list.error} onRetry={list.retry}/> : null}
    {list.items.length && !list.error ? <ConsentAutoLoad hasMore={list.hasMore} isLoading={list.isLoading} error={list.error} onLoadMore={list.loadMore}/> : null}
  </div>;
};

